import { faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { GenericDropDown } from "../../../components/Dropdown/Dropdown"

interface FinancialPaginationProps {
  page: number
  totalPages: number
  pageSize: number
  pageSizeOptions: readonly number[]
  firstItem: number
  lastItem: number
  totalItems: number
  labels: {
    ariaLabel: string
    previous: string
    next: string
    page: (current: number, total: number) => string
    range: (first: number, last: number, total: number) => string
    itemsPerPage: string
  }
  onPageChange: (page: number) => void
  onPageSizeChange: (pageSize: number) => void
}

const paginationButtonClass =
  "inline-flex min-h-10 items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2 text-xs font-extrabold text-slate-700 transition hover:border-orange-300 hover:bg-orange-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 disabled:cursor-not-allowed disabled:opacity-40"

export const FinancialPagination: React.FC<FinancialPaginationProps> = ({
  page,
  totalPages,
  pageSize,
  pageSizeOptions,
  firstItem,
  lastItem,
  totalItems,
  labels,
  onPageChange,
  onPageSizeChange,
}) => (
  <nav className="flex flex-col gap-3 border-t border-slate-200 pt-4 lg:flex-row lg:items-center lg:justify-between" aria-label={labels.ariaLabel}>
    <div className="flex flex-col gap-2 text-xs font-semibold text-slate-500 sm:flex-row sm:items-center">
      <span aria-live="polite">{labels.range(firstItem, lastItem, totalItems)}</span>
      <div className="w-full min-w-36 sm:w-auto">
        <GenericDropDown
          legend={labels.itemsPerPage}
          initialState={String(pageSize)}
          data={pageSizeOptions.map((option) => ({ label: String(option), value: String(option) }))}
          handlerFuncion={(event) => onPageSizeChange(Number(event.target.value))}
          descriptionKey="interactionDescriptions:pages.cashFlow.financialDataTable.select.pageSize"
        />
      </div>
    </div>

    <div className="flex items-center justify-between gap-2 sm:justify-end">
      <button
        type="button"
        className={paginationButtonClass}
        disabled={page <= 1}
        onClick={() => onPageChange(page - 1)}
        data-interaction-description-key="interactionDescriptions:pages.cashFlow.financialDataTable.button.previousPage"
      >
        <FontAwesomeIcon icon={faChevronLeft} />
        <span>{labels.previous}</span>
      </button>

      <span className="min-w-24 text-center text-xs font-extrabold text-slate-700" aria-live="polite">
        {labels.page(page, totalPages)}
      </span>

      <button
        type="button"
        className={paginationButtonClass}
        disabled={page >= totalPages}
        onClick={() => onPageChange(page + 1)}
        data-interaction-description-key="interactionDescriptions:pages.cashFlow.financialDataTable.button.nextPage"
      >
        <span>{labels.next}</span>
        <FontAwesomeIcon icon={faChevronRight} />
      </button>
    </div>
  </nav>
)
